import React, { useEffect, useState } from "react";
import { Play, X } from "lucide-react";
import { api } from "@/lib/api";
import { Section } from "@/components/Section";

export function parseReel(url = "") {
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) return { kind: "youtube", embed: `https://www.youtube.com/embed/${yt[1]}?autoplay=1&rel=0`, thumb: `https://img.youtube.com/vi/${yt[1]}/hqdefault.jpg` };
  const ig = url.match(/instagram\.com\/(?:reel|reels|p)\/([\w-]+)/);
  if (ig) return { kind: "instagram", embed: `https://www.instagram.com/reel/${ig[1]}/embed`, thumb: "" };
  if (/\.(mp4|webm|mov)(\?|$)/i.test(url)) return { kind: "video", embed: url, thumb: "" };
  return { kind: "unknown", embed: url, thumb: "" };
}

export function ReelPlayer({ reel, onClose }) {
  const p = parseReel(reel.video_url);
  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4" onClick={onClose} data-testid="reel-player">
      <button onClick={onClose} className="absolute top-5 right-5 text-white hover:text-[#C8102E]" data-testid="reel-player-close"><X size={28} /></button>
      <div className="w-full max-w-[420px] aspect-[9/16] bg-black border border-[#2a2a2a]" onClick={(e) => e.stopPropagation()}>
        {p.kind === "video" ? (
          <video src={p.embed} controls autoPlay playsInline className="w-full h-full object-contain" />
        ) : (
          <iframe title={reel.title} src={p.embed} className="w-full h-full" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen />
        )}
      </div>
    </div>
  );
}

export function ReelCard({ reel, onOpen }) {
  const p = parseReel(reel.video_url);
  const thumb = reel.thumbnail_url || p.thumb;
  return (
    <button onClick={() => onOpen(reel)} className="card-ive relative snap-start shrink-0 w-[200px] sm:w-[240px] aspect-[9/16] text-left group overflow-hidden" data-testid={`reel-card-${reel.id}`}>
      {thumb ? <img src={thumb} alt={reel.title} className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-[opacity,transform] duration-500" />
        : <div className="absolute inset-0 bg-[#0e0e0e]" />}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="w-14 h-14 border border-white/70 flex items-center justify-center group-hover:bg-[#C8102E] group-hover:border-[#C8102E] transition-colors"><Play size={20} fill="white" /></span>
      </div>
      <div className="absolute inset-x-0 bottom-0 p-4">
        <div className="type-meta text-[#C8102E]">{reel.tag || "Reel"}</div>
        <div className="font-display text-2xl mt-1 leading-none">{reel.title}</div>
        {reel.caption && <div className="type-meta text-[#999] mt-1 line-clamp-2">{reel.caption}</div>}
      </div>
    </button>
  );
}

export function HomeReels() {
  const [reels, setReels] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/reels", { params: { pinned: true } }).then((r) => setReels(r.data)).catch(() => {}); }, []);
  if (!reels.length) return null;
  return (
    <Section testId="home-reels" eyebrow="03 / Media Reels" title="Inside the Era">
      <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory no-scrollbar -mx-5 px-5 lg:mx-0 lg:px-0 pb-2" data-testid="reels-strip">
        {reels.map((r) => <ReelCard key={r.id} reel={r} onOpen={setOpen} />)}
      </div>
      {open && <ReelPlayer reel={open} onClose={() => setOpen(null)} />}
    </Section>
  );
}
